import { lazy, type ComponentType } from "react";

/**
 * lazyWithRetry — drop-in replacement for `React.lazy` that survives
 * stale-chunk fetch failures after a deploy.
 *
 * Why this is needed:
 *
 *   The browser holds an `index.html` that references chunks named
 *   `Keywords-hSEWZ6hR.js`. We deploy a new build → Vite emits new chunk
 *   names. The user clicks /keywords → React.lazy() tries to fetch the
 *   old hash → 404 → "Failed to fetch dynamically imported module".
 *
 *   The fix is a hard reload: that re-fetches index.html, which references
 *   the *new* chunk names, and the page works. We do this exactly once per
 *   tab session (tracked via sessionStorage) so a real chunk-load bug
 *   doesn't trigger an infinite reload loop.
 *
 * Usage in App.tsx:
 *   const Keywords = lazyWithRetry(() =>
 *     import("@/pages/Keywords").then((m) => ({ default: m.Keywords })),
 *   );
 */

const RELOAD_FLAG = "adsiq:chunk-reloaded";

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message || "";
  return (
    msg.includes("Failed to fetch dynamically imported") ||
    msg.includes("Importing a module script failed") ||
    // Safari's variant
    msg.includes("error loading dynamically imported module")
  );
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
) {
  return lazy<T>(() =>
    importFn().catch((error) => {
      if (!isChunkLoadError(error)) throw error;

      // Already retried once this session — surface the real error so
      // ErrorBoundary catches it and the user isn't trapped in a loop.
      if (sessionStorage.getItem(RELOAD_FLAG) === "1") throw error;

      sessionStorage.setItem(RELOAD_FLAG, "1");
      // Reload re-fetches index.html which references the current chunk
      // hashes. The Promise we return never resolves — the reload will
      // tear down the page first.
      window.location.reload();
      return new Promise<{ default: T }>(() => {
        /* never resolves */
      });
    }),
  );
}
